import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import coreApi from '../../utils/coreApi';
import ResumeUploadLog from '../../components/ResumeUploadLog';
import ResumeProgressBar from '../../components/ResumeProgressBar';
import {
  RESUME_AI_QUOTA_EXCEEDED,
  RESUME_PARSING_SERVICE_UNAVAILABLE,
  useResumeUploadAvailability,
} from '../../features/resume-upload/useResumeUploadAvailability';
import { RESUME_UPLOAD_CONFIG } from '../../features/resume-upload/config';
import {
  extractDroppedResumeFiles,
  isAcceptedResumeFile,
  mergeUniqueResumeFiles,
} from '../../features/resume-upload/fileUtils';
import { getResumeStatusLabel, RESUME_LIBRARY_LABEL } from '../../utils/uxLabels';

const C = {
  blue: 'var(--dashboard-blue, #3B5BDB)',
  blueLt: 'var(--dashboard-blue-soft, #EFF6FF)',
  blueMid: 'var(--dashboard-blue-border, #BFDBFE)',
  green: 'var(--dashboard-green, #059669)',
  greenLt: 'var(--dashboard-green-soft, #ECFDF5)',
  amber: 'var(--dashboard-amber, #D97706)',
  amberLt: 'var(--dashboard-amber-soft, #FFFBEB)',
  red: 'var(--dashboard-red, #DC2626)',
  redLt: 'var(--dashboard-red-soft, #FEF2F2)',
  border: 'var(--dashboard-border, #E2E8F0)',
  surface: 'var(--dashboard-surface, #F8FAFC)',
  muted: 'var(--dashboard-muted, #64748B)',
  text: 'var(--dashboard-text, #0F172A)',
};

const STATUS_STYLES = {
  success: { background: C.greenLt, color: C.green, border: 'var(--dashboard-green-border, #b0ecd4)' },
  warning: { background: C.amberLt, color: C.amber, border: 'var(--dashboard-amber-border, #fce0a0)' },
  error: { background: C.redLt, color: C.red, border: 'var(--dashboard-red-border, #fca5a5)' },
  info: { background: C.blueLt, color: C.blue, border: C.blueMid },
};

const GLASS_PANEL = {
  background: 'var(--dashboard-header-gradient, linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%))',
  border: '1px solid var(--dashboard-border, #E2E8F0)',
  boxShadow: '0 14px 32px rgba(15, 23, 42, 0.06)',
};

const GLASS_CARD = {
  background: 'var(--dashboard-card, #FFFFFF)',
  border: '1px solid var(--dashboard-border, #D7E2EC)',
  boxShadow: '0 12px 26px rgba(15, 23, 42, 0.06)',
};

const GLASS_SUBTLE = {
  background: 'var(--dashboard-card, #FFFFFF)',
  border: '1px solid var(--dashboard-border, #E2E8F2)',
  boxShadow: '0 8px 18px rgba(15, 23, 42, 0.04)',
};

function PinIcon({ color = '#5F5E5A' }) {
  return (
    <svg width="9" height="11" viewBox="0 0 10 13" fill={color} style={{ flexShrink: 0, marginTop: 1 }}>
      <path d="M5 0C2.79 0 1 1.79 1 4c0 3 4 9 4 9s4-6 4-9C9 1.79 7.21 0 5 0zm0 5.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" />
    </svg>
  );
}

const PILL_STYLES = {
  loc: { bg: 'var(--dashboard-surface, #F1EFE8)', color: 'var(--dashboard-muted, #5F5E5A)', border: 'var(--dashboard-border, #D3D1C7)' },
  src: { bg: 'var(--dashboard-blue-soft, #E6F1FB)', color: 'var(--dashboard-blue, #185FA5)', border: 'var(--dashboard-blue-border, #B5D4F4)' },
};

function MetaPill({ type = 'loc', children, icon }) {
  const style = PILL_STYLES[type] || PILL_STYLES.loc;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 10,
        fontWeight: 500,
        padding: '1px 7px',
        borderRadius: 20,
        whiteSpace: 'nowrap',
        background: style.bg,
        color: style.color,
        border: `0.5px solid ${style.border}`,
      }}
    >
      {icon}
      {children}
    </span>
  );
}

function Loader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '40px 16px', color: C.muted, fontSize: 12, ...GLASS_SUBTLE, borderRadius: 14 }}>
      <svg style={{ animation: 'spin 1s linear infinite', width: 16, height: 16 }} viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke={C.blue} strokeWidth="3" strokeDasharray="31 63" />
      </svg>
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
      Loading…
    </div>
  );
}

function Empty({ text }) {
  return (
    <div style={{ padding: '48px 20px', textAlign: 'center' }}>
      <div style={{ width: 48, height: 48, borderRadius: 12, background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
        </svg>
      </div>
      <p style={{ fontSize: 13, fontWeight: 500, color: C.text, margin: 0 }}>{text}</p>
    </div>
  );
}

function AvailabilityBanner({ title, message }) {
  return (
    <div style={{ marginTop: 16, borderRadius: 14, border: '1px solid #F4B9A2', padding: 14, background: 'linear-gradient(180deg, #FFF6F1 0%, #FFF9F6 100%)', boxShadow: '0 10px 22px rgba(194, 65, 12, 0.06)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.red} strokeWidth="2" style={{ flexShrink: 0, marginTop: 2 }}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <div>
        <p style={{ fontSize: 13, fontWeight: 700, color: C.red, margin: 0 }}>{title}</p>
        <p style={{ fontSize: 12, color: C.red, margin: '4px 0 0' }}>{message}</p>
      </div>
    </div>
  );
}

export default function DashboardResumeUploadPanel({
  user,
  onShowResume,
  uploadFileLogs,
  setUploadFileLogs,
  isParsingActive,
  setIsParsingActive,
  onRefreshStats,
}) {
  const navigate = useNavigate();
  const [tab, setTab] = useState('upload');
  const [searchQuery, setSearchQuery] = useState('');
  const [status, setStatus] = useState({ message: '', tone: 'info' });
  const [loading, setLoading] = useState(false);
  const [resumes, setResumes] = useState(null);
  const [selResume, setSelResume] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [exactDuplicates, setExactDuplicates] = useState([]);
  const [duplicateSelections, setDuplicateSelections] = useState({});
  const [isProcessingDuplicates, setIsProcessingDuplicates] = useState(false);
  const [resumeFiles, setResumeFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, phase: '' });
  const [isDragActive, setIsDragActive] = useState(false);
  const [progressTrigger, setProgressTrigger] = useState(0);

  const folderLabelRef = useRef(null);
  const folderInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const {
    aiHealthy,
    healthChecked,
    aiQuotaExceeded,
  } = useResumeUploadAvailability();

  const setStatusMessage = useCallback((message, tone = 'info') => {
    setStatus({ message, tone });
  }, []);

  useEffect(() => {
    if (isParsingActive && uploadFileLogs.length === 0) {
      const clearOldLogs = async () => {
        try {
          await fetch(`${process.env.REACT_APP_AI_AUTOMATION_URL || 'http://localhost:8100/api/v1'}/resumeparse/upload-logs/${encodeURIComponent(user.id)}/${encodeURIComponent(user.cid)}`, {
            method: 'DELETE',
          });
        } catch (error) {
          console.warn('Failed to clear old logs:', error);
        }
      };

      void clearOldLogs();
    }
  }, [isParsingActive, uploadFileLogs.length, user.id, user.cid]);

  useEffect(() => {
    if (uploadFileLogs.length > 0 && uploadFileLogs.every((log) => log.status === 'completed' || log.status === 'failed') && !showDuplicateModal) {
      setShowUploadModal(true);
    }
  }, [uploadFileLogs, showDuplicateModal]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await coreApi.post('/resume-parsing/list', { cid: user.cid, userId: user.id, page: 1, limit: 50 });
      const nextResumes = response?.data?.resumes || [];
      setResumes(nextResumes);

      setUploadFileLogs((prev) => prev.map((log) => {
        const matchingResume = nextResumes.find((resume) => (resume.file_name || resume.name) === log.file);
        if (matchingResume) {
          const nextStatus = (matchingResume.status || matchingResume.parsing_status || '').toLowerCase();
          if (nextStatus === 'parsed' || nextStatus === 'completed') {
            return { ...log, status: 'completed', message: 'Successfully analyzed' };
          }
          if (nextStatus === 'failed' || nextStatus === 'error') {
            return { ...log, status: 'failed', message: 'Parsing failed' };
          }
          if (nextStatus === 'processing') {
            return { ...log, status: 'processing', message: 'Parsing...' };
          }
          if (nextStatus === 'queue' || nextStatus === 'queued' || nextStatus === 'pending') {
            return { ...log, status: 'queue', message: 'Queued' };
          }
        }

        if (log.status === 'processing') {
          return log;
        }

        return log;
      }));

      const allCompleted = nextResumes.length > 0 && nextResumes.every((resume) => {
        const nextStatus = (resume.status || resume.parsing_status || '').toLowerCase();
        return nextStatus === 'parsed' || nextStatus === 'completed' || nextStatus === 'failed' || nextStatus === 'error';
      });

      if (allCompleted) {
        setUploadFileLogs((prev) => prev.map((log) => (
          log.status === 'processing'
            ? { ...log, status: 'completed', message: 'Successfully analyzed' }
            : log
        )));
      }
    } catch {
      setResumes([]);
    } finally {
      setLoading(false);
    }
  }, [setUploadFileLogs, user.cid, user.id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (tab === 'upload' && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [tab]);

  useEffect(() => {
    if (tab === 'upload') {
      setSearchQuery('');
    }
  }, [tab]);

  const handleProgressUpdate = useCallback((progressData) => {
    const queued = progressData?.status === 'processing';
    const fileLogs = progressData?.file_logs || progressData?.progress?.file_logs;

    if (Array.isArray(fileLogs) && fileLogs.length > 0) {
      setUploadFileLogs(fileLogs);
    }

    setIsParsingActive(queued);
  }, [setIsParsingActive, setUploadFileLogs]);

  const handleProgressComplete = useCallback((stats) => {
    setIsParsingActive(false);

    const finalLogs = stats?.file_logs || stats?.progress?.file_logs;
    if (Array.isArray(finalLogs) && finalLogs.length > 0) {
      setUploadFileLogs(finalLogs);
    }

    void load();
    onRefreshStats?.();
  }, [load, onRefreshStats, setIsParsingActive, setUploadFileLogs]);

  const validateAndAddFiles = useCallback((selectedFiles) => {
    const accepted = [];
    const rejected = [];
    const oversized = [];

    for (const file of selectedFiles) {
      if (!isAcceptedResumeFile(file)) {
        rejected.push(file.name);
      } else if (file.size > RESUME_UPLOAD_CONFIG.MAX_FILE_SIZE_MB * 1024 * 1024) {
        oversized.push(file.name);
      } else {
        accepted.push(file);
      }
    }

    if (rejected.length > 0) {
      setStatusMessage(`${rejected.length} file(s) excluded — only PDF, DOC, and DOCX files are supported.`, 'warning');
    }

    if (oversized.length > 0) {
      setStatusMessage(`${oversized.length} file(s) exceed the ${RESUME_UPLOAD_CONFIG.MAX_FILE_SIZE_MB}MB size limit and were excluded.`, 'warning');
    }

    if (accepted.length === 0) return;

    setResumeFiles((prev) => {
      const { combined, duplicateCount } = mergeUniqueResumeFiles(prev, accepted);

      if (duplicateCount > 0) {
        setStatusMessage(`${duplicateCount} duplicate file selection(s) skipped before upload.`, 'info');
      }

      if (combined.length > RESUME_UPLOAD_CONFIG.HARD_FILE_LIMIT) {
        setStatusMessage(`You've selected ${combined.length} files which exceeds the maximum of ${RESUME_UPLOAD_CONFIG.SOFT_FILE_LIMIT}. Please remove some files and try again.`, 'error');
        return prev;
      }

      const totalSizeMB = combined.reduce((sum, file) => sum + file.size, 0) / (1024 * 1024);
      if (totalSizeMB > RESUME_UPLOAD_CONFIG.MAX_TOTAL_SIZE_MB) {
        setStatusMessage(`Total file size (${Math.round(totalSizeMB)}MB) exceeds the ${RESUME_UPLOAD_CONFIG.MAX_TOTAL_SIZE_MB}MB limit. Please remove some files.`, 'error');
        return prev;
      }

      if (combined.length > RESUME_UPLOAD_CONFIG.SOFT_FILE_LIMIT) {
        setStatusMessage(`You've selected ${combined.length} files. We recommend ${RESUME_UPLOAD_CONFIG.SOFT_FILE_LIMIT} or fewer for optimal performance. Consider uploading in smaller batches next time.`, 'warning');
      }

      return combined;
    });
  }, [setStatusMessage]);

  const handleFileInputChange = (event) => {
    const files = Array.from(event.target.files);
    event.target.value = '';
    if (files.length > 0) {
      validateAndAddFiles(files);
    }
  };

  const handleFolderInputChange = useCallback((event) => {
    const allFiles = Array.from(event.target.files);
    event.target.value = '';
    const nextResumeFiles = allFiles.filter(isAcceptedResumeFile);

    if (allFiles.length > 0 && nextResumeFiles.length === 0) {
      setStatusMessage('No resume files (PDF, DOC, DOCX) found in the selected folder.', 'warning');
    } else if (nextResumeFiles.length < allFiles.length) {
      setStatusMessage(`Found ${nextResumeFiles.length} resume file(s) out of ${allFiles.length} total files in the folder.`, 'info');
    }

    if (nextResumeFiles.length > 0) {
      validateAndAddFiles(nextResumeFiles);
    }
  }, [setStatusMessage, validateAndAddFiles]);

  const removeFile = (index) => {
    setResumeFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index));
  };

  const clearSelectedFiles = useCallback(() => {
    setResumeFiles([]);
    setStatusMessage('Selected files cleared.', 'info');
  }, [setStatusMessage]);

  const handleDrop = useCallback(async (event) => {
    event.preventDefault();
    setIsDragActive(false);

    const droppedResumeFiles = await extractDroppedResumeFiles(event.dataTransfer);
    const allDroppedFiles = Array.from(event.dataTransfer?.files || []);

    if (allDroppedFiles.length > 0 && droppedResumeFiles.length === 0) {
      setStatusMessage('No resume files (PDF, DOC, DOCX) found in the dropped selection.', 'warning');
      return;
    }

    if (allDroppedFiles.length > droppedResumeFiles.length) {
      setStatusMessage(`Found ${droppedResumeFiles.length} resume file(s) out of ${allDroppedFiles.length} dropped item(s).`, 'info');
    }

    if (droppedResumeFiles.length > 0) {
      validateAndAddFiles(droppedResumeFiles);
    }
  }, [setStatusMessage, validateAndAddFiles]);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setIsDragActive(false);
  }, []);

  const handleUpload = async () => {
    if (resumeFiles.length === 0) {
      setStatusMessage('Please select at least one resume file before uploading.', 'warning');
      return;
    }

    if (!user.id) {
      setStatusMessage('Your session has expired. Please log in again to continue.', 'error');
      return;
    }

    if (!user.cid) {
      setStatusMessage('Company profile is required. Redirecting you to complete the setup...', 'warning');
      return;
    }

    if (!aiHealthy) {
      setStatusMessage(RESUME_PARSING_SERVICE_UNAVAILABLE.message, 'error');
      return;
    }

    if (aiQuotaExceeded) {
      setStatusMessage(RESUME_AI_QUOTA_EXCEEDED.message, 'error');
      return;
    }

    const totalFiles = resumeFiles.length;
    const totalChunks = Math.ceil(totalFiles / RESUME_UPLOAD_CONFIG.CHUNK_SIZE);

    setIsUploading(true);
    setUploadProgress({ current: 0, total: totalFiles, phase: 'uploading' });

    if (!isParsingActive) {
      setUploadFileLogs([]);
    }

    let uploadedCount = 0;
    let failedCount = 0;
    let allExactDuplicates = [];
    let hasParsingStarted = false;

    try {
      for (let index = 0; index < totalChunks; index += 1) {
        const chunkFiles = resumeFiles.slice(index * RESUME_UPLOAD_CONFIG.CHUNK_SIZE, (index + 1) * RESUME_UPLOAD_CONFIG.CHUNK_SIZE);

        const formData = new FormData();
        chunkFiles.forEach((file) => formData.append('file', file));
        formData.append('userId', String(user.id));
        formData.append('cid', String(user.cid));
        formData.append('chunk_index', index.toString());
        formData.append('total_chunks', totalChunks.toString());
        formData.append('total_files', totalFiles.toString());

        try {
          const result = await coreApi.upload('/resume-parsing/upload', formData);

          uploadedCount += chunkFiles.length;
          setUploadProgress({ current: uploadedCount, total: totalFiles, phase: 'uploading' });

          if (result.data?.exact_duplicates?.length > 0) {
            allExactDuplicates = [...allExactDuplicates, ...result.data.exact_duplicates];
          }

          if (result.data?.status !== 'upload_only') {
            hasParsingStarted = true;
          }
        } catch (chunkError) {
          console.error(`Chunk ${index + 1}/${totalChunks} failed:`, chunkError);
          failedCount += chunkFiles.length;
        }
      }

      const newCount = uploadedCount - allExactDuplicates.length;
      if (failedCount > 0) {
        setStatusMessage(`${uploadedCount} of ${totalFiles} file(s) uploaded. ${failedCount} failed — please retry those files.`, 'warning');
      } else if (allExactDuplicates.length > 0 && newCount === 0) {
        setStatusMessage(`${allExactDuplicates.length} duplicate resume(s) detected. Review them below before re-processing.`, 'warning');
      } else if (!hasParsingStarted) {
        setStatusMessage('Files uploaded but the resume parsing service is currently unavailable. Parsing will retry automatically when the service is back.', 'warning');
      } else if (allExactDuplicates.length > 0) {
        setStatusMessage(`${newCount} new resume(s) processing. ${allExactDuplicates.length} duplicate(s) need your review.`, 'success');
      } else {
        setStatusMessage(`${newCount} resume(s) uploaded! Parsing in progress — results will appear in Analytics & Database once ready.`, 'success');
      }

      setResumeFiles([]);
      setUploadProgress({ current: 0, total: 0, phase: '' });
      setIsParsingActive(hasParsingStarted);
      void load();
      onRefreshStats?.();

      if (hasParsingStarted) {
        setProgressTrigger((prev) => prev + 1);
      }

      if (allExactDuplicates.length > 0) {
        setExactDuplicates(allExactDuplicates);
        const selections = {};
        allExactDuplicates.forEach((_, duplicateIndex) => {
          selections[duplicateIndex] = true;
        });
        setDuplicateSelections(selections);
        setShowDuplicateModal(true);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setStatusMessage(error.message || 'Failed to upload resumes. Please check your connection and try again.', 'error');
    } finally {
      setIsUploading(false);
      setUploadProgress({ current: 0, total: 0, phase: '' });
    }
  };

  useEffect(() => {
    const folderInput = document.createElement('input');
    folderInput.type = 'file';
    folderInput.webkitdirectory = true;
    folderInput.directory = true;
    folderInput.style.display = 'none';
    folderInput.multiple = true;

    folderInputRef.current = folderInput;
    document.body.appendChild(folderInput);

    return () => {
      if (folderInputRef.current) {
        document.body.removeChild(folderInputRef.current);
        folderInputRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (folderLabelRef.current && folderInputRef.current) {
      folderLabelRef.current.onclick = (event) => {
        event.preventDefault();
        folderInputRef.current.click();
      };
    }
  }, []);

  useEffect(() => {
    if (folderInputRef.current) {
      folderInputRef.current.onchange = handleFolderInputChange;
    }
  }, [handleFolderInputChange]);

  const filtered = (resumes || []).filter((resume) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;

    const haystack = [
      resume.file_name,
      resume.name,
      resume.email,
      resume.candidate_email,
      resume.location,
      resume.phone,
      resume.title,
      resume.role,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return haystack.includes(query);
  });
  const resumeCount = Array.isArray(resumes) ? resumes.length : 0;
  const openResumeLibraryFullView = useCallback(() => {
    navigate('/resume-parsing?from_dashboard=true');
  }, [navigate]);
  const statusStyle = STATUS_STYLES[status.tone] || STATUS_STYLES.info;
  const uploadDisabled = isUploading || aiQuotaExceeded || (healthChecked && !aiHealthy);
  const selectedTotalSizeMb = resumeFiles.reduce((sum, file) => sum + file.size, 0) / (1024 * 1024);

  const toggleDuplicateSelection = (index) => {
    setDuplicateSelections((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const toggleAllDuplicates = () => {
    const allSelected = Object.values(duplicateSelections).every(Boolean);
    const nextSelections = {};
    exactDuplicates.forEach((_, index) => {
      nextSelections[index] = !allSelected;
    });
    setDuplicateSelections(nextSelections);
  };

  const handleDuplicateDecision = async (action) => {
    const selectedCount = Object.values(duplicateSelections).filter(Boolean).length;
    const totalCount = exactDuplicates.length;

    if (action === 'skip') {
      const skippedCount = selectedCount || totalCount;
      setStatusMessage(`${skippedCount} duplicate resume(s) skipped — existing records kept as-is.`, 'warning');
      setShowDuplicateModal(false);
      setExactDuplicates([]);
      return;
    }

    setIsProcessingDuplicates(true);
    try {
      const duplicates = exactDuplicates.map((duplicate, index) => ({
        existing_doc_id: duplicate.existing_doc_id,
        file_hash: duplicate.file_hash,
        action: duplicateSelections[index] ? 'reparse' : 'skip',
      }));

      const result = await coreApi.post('/resume-parsing/process-duplicates', {
        cid: user.cid,
        userId: user.id,
        duplicates,
      });

      const data = result.data;
      const reparseCount = data?.reparse_count || 0;
      const skippedCount = data?.skipped_count || 0;

      if (reparseCount > 0 && skippedCount > 0) {
        setStatusMessage(`${reparseCount} resume(s) sent for re-parsing, ${skippedCount} skipped.`, 'success');
      } else if (reparseCount > 0) {
        setStatusMessage(`${reparseCount} duplicate resume(s) sent for re-parsing. Results will appear shortly.`, 'success');
      } else {
        setStatusMessage(`${skippedCount} duplicate resume(s) skipped — existing records kept as-is.`, 'warning');
      }

      if (reparseCount > 0) {
        setIsParsingActive(true);
        setProgressTrigger((prev) => prev + 1);
        const maxAttempts = 30;
        let attempts = 0;

        const pollInterval = setInterval(async () => {
          attempts += 1;
          try {
            const statusResponse = await coreApi.post('/resume-parsing/list', { cid: user.cid, userId: user.id, page: 1, limit: 50 });
            const nextResumes = statusResponse?.data?.resumes || [];
            const allCompleted = nextResumes.every((resume) => {
              const nextStatus = (resume.status || resume.parsing_status || '').toLowerCase();
              return nextStatus === 'parsed' || nextStatus === 'completed' || nextStatus === 'failed' || nextStatus === 'error';
            });

            if (allCompleted || attempts >= maxAttempts) {
              clearInterval(pollInterval);
              void load();
              onRefreshStats?.();
              setStatusMessage(`${reparseCount} duplicate resume(s) re-parsed successfully. (Note: Re-parsing updates existing records - count remains the same)`, 'success');
            }
          } catch (error) {
            console.error('Polling error:', error);
            clearInterval(pollInterval);
            void load();
            onRefreshStats?.();
          }
        }, 5000);
      } else {
        void load();
        onRefreshStats?.();
      }
    } catch (error) {
      console.error('Process duplicates error:', error);
      setStatusMessage('Something went wrong while processing duplicates. Please try uploading again.', 'error');
    } finally {
      setIsProcessingDuplicates(false);
      setShowDuplicateModal(false);
      setExactDuplicates([]);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--dashboard-content-gradient, linear-gradient(180deg, #F8FAFD 0%, #F3F7FC 100%))' }}>
      <div style={{ padding: '12px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', gap: 6, ...GLASS_PANEL }}>
        {['upload', 'database'].map((nextTab) => (
          <button
            key={nextTab}
            onClick={() => setTab(nextTab)}
            style={{
              borderRadius: 8,
              padding: '5px 12px',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              textTransform: 'capitalize',
              background: tab === nextTab ? 'linear-gradient(135deg, #3B5BDB 0%, #28ACA8 100%)' : 'var(--dashboard-card, #FFFFFF)',
              color: tab === nextTab ? '#fff' : C.muted,
              border: tab === nextTab ? '1px solid rgba(29, 78, 216, 0.12)' : `1px solid ${C.border}`,
              boxShadow: tab === nextTab ? '0 10px 22px rgba(29, 78, 216, 0.18)' : 'none',
            }}
          >
            {nextTab}
          </button>
        ))}
      </div>

      {tab === 'upload' ? (
        <div ref={scrollContainerRef} style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 24, overflowY: 'auto' }}>
          <div style={{ ...GLASS_PANEL, borderRadius: 16, padding: '12px 14px', marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: C.blue, boxShadow: `0 0 0 4px ${C.blue}18`, flexShrink: 0 }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text, lineHeight: 1.2 }}>{RESUME_LIBRARY_LABEL}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Upload, analyze, and open candidate profiles from one place.</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 12px', borderRadius: 999, background: C.surface, border: `1px solid ${C.border}`, color: C.blue, fontSize: 11, fontWeight: 700 }}>
                  <span>{resumeCount}</span>
                  <span style={{ color: C.muted, fontWeight: 600 }}>Profiles available</span>
                </div>
                <button
                  type="button"
                  onClick={openResumeLibraryFullView}
                  style={{
                    borderRadius: 999,
                    padding: '8px 14px',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer',
                    border: `1px solid ${C.blueMid}`,
                    background: 'var(--dashboard-card, #FFFFFF)',
                    color: C.blue,
                    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.04)',
                  }}
                >
                  Open Full View
                </button>
              </div>
            </div>
          </div>

          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            style={{
              borderRadius: 18,
              border: `2px dashed ${isDragActive ? C.blue : C.blueMid}`,
              padding: '32px 24px',
              textAlign: 'center',
              background: isDragActive ? 'var(--dashboard-hover, linear-gradient(180deg, #F1F6FF 0%, #FFFFFF 100%))' : 'var(--dashboard-card, linear-gradient(180deg, #FFFFFF 0%, #F8FAFD 100%))',
              transition: 'all 0.15s ease',
              boxShadow: isDragActive ? '0 16px 30px rgba(29, 78, 216, 0.10)' : '0 12px 26px rgba(15, 23, 42, 0.04)',
            }}
          >
            <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, #3B5BDB 0%, #28ACA8 100%)', boxShadow: '0 10px 22px rgba(59, 91, 219, 0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 16V4m0 0l-4 4m4-4l4 4M5 20h14" />
              </svg>
            </div>

            <p style={{ fontSize: 13, fontWeight: 600, color: C.text, margin: '0 0 4px' }}>
              {isDragActive ? 'Drop resumes to add them to the upload queue' : 'Drag & drop files or folders here'}
            </p>
            <p style={{ fontSize: 11, color: C.muted, margin: '0 0 16px' }}>
              PDF, DOC, DOCX supported. Recommended batch: {RESUME_UPLOAD_CONFIG.SOFT_FILE_LIMIT} files or fewer.
            </p>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <label style={{ borderRadius: 10, padding: '8px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer', background: 'linear-gradient(135deg, #3B5BDB 0%, #28ACA8 100%)', color: '#fff', border: '1px solid rgba(59, 91, 219, 0.16)', boxShadow: '0 10px 20px rgba(59, 91, 219, 0.14)' }}>
                Select Files
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: 'none' }}
                  accept=".pdf,.doc,.docx"
                  multiple
                  onChange={handleFileInputChange}
                />
              </label>

              <label
                ref={folderLabelRef}
                style={{
                  borderRadius: 10,
                  padding: '8px 16px',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: 'var(--dashboard-card, #FFFFFF)',
                  color: C.text,
                  border: `1px solid ${C.border}`,
                  boxShadow: '0 8px 18px rgba(15, 23, 42, 0.04)',
                }}
              >
                Select Folder
              </label>
            </div>
          </div>

          {healthChecked && !aiHealthy && (
            <AvailabilityBanner
              title={RESUME_PARSING_SERVICE_UNAVAILABLE.title}
              message={RESUME_PARSING_SERVICE_UNAVAILABLE.message}
            />
          )}

          {aiQuotaExceeded && (
            <AvailabilityBanner
              title={RESUME_AI_QUOTA_EXCEEDED.title}
              message={RESUME_AI_QUOTA_EXCEEDED.message}
            />
          )}

          {user.id && user.cid && !(healthChecked && !aiHealthy) && (
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
              <ResumeProgressBar
                userId={user.id}
                cid={user.cid}
                onComplete={handleProgressComplete}
                onProgressUpdate={handleProgressUpdate}
                triggerCheck={progressTrigger}
              />
            </div>
          )}

          {resumeFiles.length > 0 && (
            <div style={{ marginTop: 16, borderRadius: 16, padding: 16, ...GLASS_CARD }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div>
                  <p style={{ fontSize: 13, fontWeight: 600, color: C.text, margin: 0 }}>
                    {resumeFiles.length} file{resumeFiles.length !== 1 ? 's' : ''} selected
                  </p>
                  <p style={{ fontSize: 11, color: C.muted, margin: '4px 0 0' }}>
                    Total size: {selectedTotalSizeMb.toFixed(1)} MB
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    onClick={clearSelectedFiles}
                    disabled={isUploading}
                    style={{
                      borderRadius: 10,
                      padding: '8px 12px',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: isUploading ? 'default' : 'pointer',
                      background: 'var(--dashboard-card, #FFFFFF)',
                      color: C.text,
                      border: `1px solid ${C.border}`,
                      boxShadow: '0 8px 18px rgba(15, 23, 42, 0.04)',
                    }}
                  >
                    Clear
                  </button>
                  <button
                    onClick={handleUpload}
                    disabled={uploadDisabled}
                    style={{
                      borderRadius: 10,
                      padding: '8px 16px',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: uploadDisabled ? 'default' : 'pointer',
                      background: uploadDisabled ? C.blueLt : 'linear-gradient(135deg, #3B5BDB 0%, #28ACA8 100%)',
                      color: uploadDisabled ? C.muted : '#fff',
                      border: '1px solid rgba(29, 78, 216, 0.16)',
                      boxShadow: uploadDisabled ? 'none' : '0 10px 22px rgba(29, 78, 216, 0.14)',
                    }}
                  >
                    {isUploading ? `Uploading... ${uploadProgress.current}/${uploadProgress.total}` : 'Upload Resumes'}
                  </button>
                </div>
              </div>
              <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                {resumeFiles.map((file, index) => (
                <div key={`${file.name}-${index}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: index < resumeFiles.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                  <span style={{ fontSize: 12, color: C.text }}>{file.name}</span>
                  <span style={{ fontSize: 11, color: C.muted }}>{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                    <button onClick={() => removeFile(index)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.red} strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {loading && (
            <div style={{ marginTop: 16, borderRadius: 12, border: `1px solid ${C.blue}`, padding: 16, background: C.blueLt }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.blue} strokeWidth="2" className="animate-spin">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" />
                </svg>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.blue }}>Uploading…</span>
              </div>
            </div>
          )}

          {status.message && (
            <div style={{ marginTop: 12, borderRadius: 10, padding: '8px 14px', fontSize: 12, background: statusStyle.background, color: statusStyle.color, border: `1px solid ${statusStyle.border}` }}>
              {status.message}
            </div>
          )}

          {user.id && user.cid && (
            <div style={{ marginTop: 16 }}>
              <ResumeUploadLog
                userId={user.id}
                cid={user.cid}
                fileLogs={uploadFileLogs}
                isProcessing={isParsingActive}
              />
            </div>
          )}

          {resumes !== null && (() => {
            const logsNotInDb = uploadFileLogs.filter((log) => !resumes.some((resume) => (resume.file_name || resume.name) === log.file) && log.status !== 'duplicate');
            const total = resumes.length + logsNotInDb.length;
            const parsed = resumes.filter((resume) => ['parsed', 'completed'].includes((resume.status || resume.parsing_status || '').toLowerCase())).length
              + uploadFileLogs.filter((log) => log.status === 'completed' && !resumes.some((resume) => (resume.file_name || resume.name) === log.file) && log.status !== 'duplicate').length;
            const failed = resumes.filter((resume) => ['failed', 'error'].includes((resume.status || resume.parsing_status || '').toLowerCase())).length
              + uploadFileLogs.filter((log) => log.status === 'failed' && !resumes.some((resume) => (resume.file_name || resume.name) === log.file) && log.status !== 'duplicate').length;
            const queuedDb = resumes.filter((resume) => ['queued', 'processing', 'pending'].includes((resume.status || resume.parsing_status || '').toLowerCase())).length;
            const queuedLogs = uploadFileLogs.filter((log) => ['processing', 'queue', 'queued'].includes(log.status) && !resumes.some((resume) => (resume.file_name || resume.name) === log.file) && log.status !== 'duplicate').length;
            const queued = queuedDb + queuedLogs;

            const bars = [
              { label: 'Analyzed', count: parsed, color: C.green },
              { label: 'Queued / Processing', count: queued, color: C.amber },
              { label: 'Failed', count: failed, color: C.red },
            ];

            return (
              <>
                <div style={{ marginTop: 20, marginBottom: 8, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: C.muted }}>Analytics</div>
                <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                  <div style={{ flex: 1, borderRadius: 12, border: `1px solid ${C.border}`, padding: '12px 14px', background: C.surface }}>
                    <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: C.muted, margin: '0 0 4px' }}>Total Resumes</p>
                    <p style={{ fontSize: 24, fontWeight: 700, color: C.blue, margin: 0 }}>{total}</p>
                  </div>
                  <div style={{ flex: 1, borderRadius: 12, border: `1px solid ${C.border}`, padding: '12px 14px', background: C.greenLt }}>
                    <p style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: C.muted, margin: '0 0 4px' }}>Successfully Analyzed</p>
                    <p style={{ fontSize: 24, fontWeight: 700, color: C.green, margin: 0 }}>{parsed}</p>
                  </div>
                </div>
                {bars.map((bar) => (
                  <div key={bar.label} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: bar.color }}>{bar.label}</span>
                      <span style={{ fontSize: 11, color: C.muted }}>{bar.count} ({total ? Math.round((bar.count / total) * 100) : 0}%)</span>
                    </div>
                    <div style={{ height: 8, borderRadius: 4, background: C.surface, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${total && bar.count > 0 ? (bar.count / total) * 100 : 0}%`, minWidth: bar.count > 0 ? '2px' : 0, borderRadius: 4, background: bar.color, transition: 'width .3s' }} />
                    </div>
                  </div>
                ))}
                {total === 0 && <Empty text="No resumes uploaded yet" />}
              </>
            );
          })()}
        </div>
      ) : (
        <>
          <div style={{ padding: '14px 14px 6px' }}>
            <div style={{ ...GLASS_PANEL, borderRadius: 16, padding: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Candidate Library</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>Search and open analyzed profiles from your database.</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 12px', borderRadius: 999, background: C.surface, border: `1px solid ${C.border}`, color: C.blue, fontSize: 11, fontWeight: 700 }}>
                    <span>{filtered.length}</span>
                    <span style={{ color: C.muted, fontWeight: 600 }}>matching profiles</span>
                  </div>
                  <button
                    type="button"
                    onClick={openResumeLibraryFullView}
                    style={{
                      borderRadius: 999,
                      padding: '8px 14px',
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: 'pointer',
                      border: `1px solid ${C.blueMid}`,
                      background: 'var(--dashboard-card, #FFFFFF)',
                      color: C.blue,
                      boxShadow: '0 8px 18px rgba(15, 23, 42, 0.04)',
                    }}
                  >
                    Open Full View
                  </button>
                </div>
              </div>

              <div style={{ position: 'relative' }}>
                <svg style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <circle cx="7" cy="7" r="5" stroke="#94A3B8" strokeWidth="1.4" />
                  <path d="M11 11l3 3" stroke="#94A3B8" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
                <input
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search by name, file, email, or location..."
                  style={{
                    width: '100%',
                    background: 'var(--dashboard-card, #FFFFFF)',
                    border: `1px solid ${C.border}`,
                    borderRadius: 14,
                    padding: '10px 14px 10px 36px',
                    fontSize: 12,
                    outline: 'none',
                    color: C.text,
                    fontFamily: 'inherit',
                    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.04)',
                  }}
                />
              </div>
            </div>
          </div>

          <div style={{ padding: '0 14px 2px', fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: C.muted }}>{filtered.length} resumes in database</div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px' }}>
            {resumes === null ? <Loader /> : filtered.length === 0 ? <Empty text="No resumes found" /> : filtered.map((resume, index) => {
              const avatarColors = [
                { bg: C.blueLt, color: C.blue },
                { bg: C.greenLt, color: C.green },
                { bg: C.amberLt, color: C.amber },
                { bg: C.blueLt, color: C.blue },
                { bg: C.redLt, color: C.red },
              ];

              const statusColors = {
                parsed: { bg: C.greenLt, color: C.green },
                completed: { bg: C.greenLt, color: C.green },
                failed: { bg: C.redLt, color: C.red },
                error: { bg: C.redLt, color: C.red },
                queued: { bg: C.surface, color: C.muted },
                processing: { bg: C.amberLt, color: C.amber },
                pending: { bg: C.amberLt, color: C.amber },
              };

              const avatar = avatarColors[index % avatarColors.length];
              const statusKey = (resume.status || resume.parsing_status || 'queued').toLowerCase();
              const badge = statusColors[statusKey] || statusColors.queued;
              const fileName = resume.file_name || resume.name || 'Resume';
              const email = resume.email || resume.candidate_email || '';

              return (
                <React.Fragment key={resume.id || index}>
                  <div
                    onClick={() => {
                      setSelResume(resume.id || index);
                      onShowResume?.(resume);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '12px 14px',
                      borderRadius: 12,
                      marginBottom: 8,
                      border: selResume === (resume.id || index) ? `1px solid ${C.blue}` : `1px solid ${C.border}`,
                      background: selResume === (resume.id || index) ? 'var(--dashboard-hover, linear-gradient(180deg, #F2F7FF 0%, #FFFFFF 100%))' : 'var(--dashboard-card, linear-gradient(180deg, #FFFFFF 0%, #F9FBFD 100%))',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: selResume === (resume.id || index) ? '0 12px 24px rgba(29, 78, 216, 0.10)' : '0 8px 18px rgba(15, 23, 42, 0.04)',
                    }}
                    onMouseEnter={(event) => {
                      if (selResume !== (resume.id || index)) {
                        event.currentTarget.style.background = 'var(--dashboard-hover, linear-gradient(180deg, #F3F7FF 0%, #FFFFFF 100%))';
                        event.currentTarget.style.borderColor = 'var(--dashboard-blue-border, #C7D2FE)';
                        event.currentTarget.style.transform = 'translateY(-2px)';
                        event.currentTarget.style.boxShadow = '0 14px 28px rgba(29, 78, 216, 0.10)';
                      }
                    }}
                    onMouseLeave={(event) => {
                      if (selResume !== (resume.id || index)) {
                        event.currentTarget.style.background = 'var(--dashboard-card, linear-gradient(180deg, #FFFFFF 0%, #F9FBFD 100%))';
                        event.currentTarget.style.borderColor = 'var(--dashboard-border, #E2E8F2)';
                        event.currentTarget.style.transform = 'translateY(0)';
                        event.currentTarget.style.boxShadow = '0 8px 18px rgba(15, 23, 42, 0.04)';
                      }
                    }}
                  >
                    <div style={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0, background: avatar.bg, color: avatar.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 500 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 6 }}>
                        {fileName}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                        {email ? <MetaPill type="src">{email}</MetaPill> : null}
                        {resume.location ? <MetaPill type="loc" icon={<PinIcon />}>{resume.location}</MetaPill> : null}
                      </div>
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 500, padding: '4px 10px', borderRadius: 20, whiteSpace: 'nowrap', flexShrink: 0, background: badge.bg, color: badge.color, border: `0.5px solid ${badge.color}22` }}>
                      {getResumeStatusLabel(statusKey)}
                    </span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </>
      )}

      {showDuplicateModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 16, width: '90%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto', padding: 24 }}>
            <div style={{ marginBottom: 16 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: C.text }}>Duplicates Found</h2>
              <p style={{ fontSize: 13, color: C.muted, margin: '4px 0 0' }}>
                {exactDuplicates.length} file{exactDuplicates.length > 1 ? 's' : ''} already exist{exactDuplicates.length > 1 ? '' : 's'} in your database
              </p>
            </div>

            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={Object.values(duplicateSelections).every(Boolean)} onChange={toggleAllDuplicates} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>Select All</span>
              </label>
              <span style={{ fontSize: 11, color: C.muted }}>
                {Object.values(duplicateSelections).filter(Boolean).length} of {exactDuplicates.length} selected
              </span>
            </div>

            <div style={{ marginBottom: 20, maxHeight: 300, overflowY: 'auto', border: `1px solid ${C.border}`, borderRadius: 8 }}>
              {exactDuplicates.map((duplicate, index) => (
                <div key={`${duplicate.file_hash || duplicate.uploaded_file}-${index}`} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, borderBottom: index < exactDuplicates.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                  <input type="checkbox" checked={duplicateSelections[index] || false} onChange={() => toggleDuplicateSelection(index)} style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: C.text, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {duplicate.uploaded_file || 'Unknown file'}
                    </p>
                    <div style={{ display: 'flex', gap: 16, marginTop: 2 }}>
                      <span style={{ fontSize: 11, color: C.muted }}>Matches: {duplicate.existing_name || 'Unknown'}</span>
                      {duplicate.uploaded_at ? (
                        <span style={{ fontSize: 11, color: C.muted }}>
                          Uploaded: {new Date(duplicate.uploaded_at).toLocaleDateString()}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => handleDuplicateDecision('skip')} disabled={isProcessingDuplicates} style={{ padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: C.surface, color: C.text, border: `1px solid ${C.border}`, cursor: isProcessingDuplicates ? 'not-allowed' : 'pointer' }}>
                Skip ({Object.values(duplicateSelections).filter(Boolean).length || exactDuplicates.length})
              </button>
              <button onClick={() => handleDuplicateDecision('reparse')} disabled={isProcessingDuplicates} style={{ padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: C.blue, color: '#fff', border: 'none', cursor: isProcessingDuplicates ? 'not-allowed' : 'pointer' }}>
                {isProcessingDuplicates ? 'Processing...' : `Re-parse (${Object.values(duplicateSelections).filter(Boolean).length || exactDuplicates.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showUploadModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 16, width: '90%', maxWidth: 500, maxHeight: '90vh', overflowY: 'auto', padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: C.text }}>Upload Status</h2>
              <button onClick={() => setShowUploadModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <ResumeUploadLog
              userId={user.id}
              cid={user.cid}
              fileLogs={uploadFileLogs}
              isProcessing={isParsingActive || (uploadFileLogs && uploadFileLogs.some((log) => log.status === 'processing' || log.status === 'queue'))}
            />
            {uploadFileLogs.length > 0 && uploadFileLogs.every((log) => log.status === 'completed' || log.status === 'failed') && (
              <button onClick={() => setShowUploadModal(false)} style={{ marginTop: 20, width: '100%', padding: '12px', borderRadius: 8, fontSize: 14, fontWeight: 600, background: C.blue, color: '#fff', border: 'none', cursor: 'pointer' }}>
                Okay
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
